/*
 * Topbar campaign controller.
 *
 * Reads the campaign config (already filtered to the current page server-side),
 * activates the first campaign whose time window is currently open, applies the
 * signal color / gradient / pill / link, rotates 1-3 campaign texts using the
 * theme's existing <promo-rotator> effect, and fires a one-shot confetti burst
 * (once per session, after a configurable delay).
 *
 * Scheduling runs on the client against absolute epoch timestamps, so it is
 * cache-safe and switches exactly on time without a reload.
 */
if (!customElements.get('topbar-campaign')) {
  customElements.define(
    'topbar-campaign',
    class TopbarCampaign extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section') || document;
        this.topbar = this.section.querySelector('.topbar');
        this.slide = this.topbar && this.topbar.querySelector('.topbar__campaign-slide');
        this.campaigns = this._readConfig();
        this._boundaryTimers = [];
        this._activeId = null;
        this._forcedId = null;

        if (!this.topbar || !this.slide || !this.campaigns.length) return;

        this._evaluate();
        this._scheduleBoundaries();

        // Pause the (infinite) shine/pill animations while the tab is hidden.
        this._visHandler = () => {
          if (!this.topbar) return;
          this.topbar.classList.toggle('topbar--campaign-paused', document.hidden);
        };
        document.addEventListener('visibilitychange', this._visHandler);

        // In the theme editor, preview whichever campaign block the merchant selects.
        if (this._designMode) {
          this._selectHandler = (e) => {
            const id = e && e.detail && e.detail.blockId;
            if (!id) return;
            if (this.campaigns.some((c) => c.id === id)) {
              this._forcedId = id;
              this._evaluate();
            }
          };
          document.addEventListener('shopify:block:select', this._selectHandler);
        }
      }

      disconnectedCallback() {
        (this._boundaryTimers || []).forEach(clearTimeout);
        if (this._confettiTimer) clearTimeout(this._confettiTimer);
        if (this._visHandler) document.removeEventListener('visibilitychange', this._visHandler);
        if (this._selectHandler) document.removeEventListener('shopify:block:select', this._selectHandler);
      }

      get _designMode() {
        return !!(window.Shopify && window.Shopify.designMode);
      }

      get _preview() {
        // Test override: ?tb_campaign forces the first campaign on any page,
        // ignoring page targeting and the time window.
        try {
          return new URLSearchParams(window.location.search).has('tb_campaign');
        } catch (e) {
          return false;
        }
      }

      _readConfig() {
        const node = this.querySelector('script[type="application/json"]');
        if (!node) return [];
        try {
          const parsed = JSON.parse(node.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }

      _now() {
        return Date.now() / 1000;
      }

      _timeActive() {
        const now = this._now();
        return (
          this.campaigns.find((c) => {
            if (!c.match) return false;
            const startOk = c.start == null || now >= c.start;
            const endOk = c.end == null || now < c.end;
            return startOk && endOk;
          }) || null
        );
      }

      _pickActive() {
        if (this._designMode) {
          if (this._forcedId) {
            const forced = this.campaigns.find((c) => c.id === this._forcedId);
            if (forced) return forced;
          }
          return this._timeActive() || this.campaigns[0] || null;
        }
        if (this._preview) return this.campaigns[0] || null;
        return this._timeActive();
      }

      _evaluate() {
        const active = this._pickActive();
        if (active) {
          this._activate(active);
        } else {
          this._deactivate();
        }
      }

      _brightness(hex) {
        hex = (hex || '').replace('#', '');
        if (hex.length === 3) {
          hex = hex.replace(/./g, '$&$&');
        }
        const r = parseInt(hex.substr(0, 2), 16) || 0;
        const g = parseInt(hex.substr(2, 2), 16) || 0;
        const b = parseInt(hex.substr(4, 2), 16) || 0;
        return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      }

      _makeSlide(c, textHtml) {
        // Slides hold only the rotating text + pill. The icon and the link live
        // once, outside the rotator, so the icon stays put while text rotates.
        const wrapper = document.createElement('span');
        wrapper.className = 'topbar__promo-slide rte';

        const inner = document.createElement('span');
        inner.className = 'topbar__promo-slide__inner';

        const text = document.createElement('span');
        text.className = 'topbar__promo-slide__text';
        text.innerHTML = textHtml || '';
        inner.appendChild(text);

        if (c.tag) {
          const tag = document.createElement('span');
          tag.className = 'topbar__campaign-tag';
          tag.textContent = c.tag;
          inner.appendChild(tag);
        }

        wrapper.appendChild(inner);
        return wrapper;
      }

      _buildRotator(c) {
        const texts = c.texts && c.texts.length ? c.texts : [''];

        // One link wraps the fixed icon and the rotating text/pill, so hovering
        // anywhere over the campaign triggers the icon animation.
        const link = document.createElement(c.link ? 'a' : 'span');
        link.className = 'topbar__campaign-link' + (c.anim ? ' tb-anim-trigger' : '');
        if (c.link) link.href = c.link;

        if (c.icon) {
          if (c.anim) {
            // Animated icon markup already carries its own .tb-anim-icon wrapper.
            link.insertAdjacentHTML('beforeend', c.icon);
          } else {
            const icon = document.createElement('span');
            icon.className = 'topbar__campaign-icon';
            icon.setAttribute('aria-hidden', 'true');
            icon.innerHTML = c.icon;
            link.appendChild(icon);
          }
        }

        const rotator = document.createElement('promo-rotator');
        rotator.className = 'topbar__promo-rotator topbar__campaign-rotator';
        rotator.dataset.count = texts.length;
        rotator.dataset.interval = c.interval || 6000;
        texts.forEach((t) => rotator.appendChild(this._makeSlide(c, t)));
        link.appendChild(rotator);

        // Appending triggers PromoRotator.connectedCallback -> rotation starts.
        this.slide.innerHTML = '';
        this.slide.appendChild(link);

        this._sizeRotator(rotator);
        this._playIconOnce(link);
      }

      _sizeRotator(rotator) {
        // Slides are position:absolute, so the rotator has no intrinsic width and
        // would collapse (wrapping the text and blowing up the bar height). Measure
        // each slide's natural single-line width off-layout and lock the rotator.
        const slides = Array.from(rotator.querySelectorAll('.topbar__promo-slide__inner'));
        if (!slides.length) return;
        const measure = () => {
          const probe = document.createElement('div');
          probe.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;width:max-content;';
          document.body.appendChild(probe);
          let max = 0;
          slides.forEach((s) => {
            const clone = s.cloneNode(true);
            clone.style.width = 'max-content';
            probe.appendChild(clone);
            const w = clone.getBoundingClientRect().width;
            if (w > max) max = w;
            probe.removeChild(clone);
          });
          document.body.removeChild(probe);
          if (max > 0) {
            const cap = Math.max(160, Math.floor(window.innerWidth * 0.9));
            rotator.style.width = `${Math.min(Math.ceil(max), cap)}px`;
          }
        };
        requestAnimationFrame(measure);
      }

      _playIconOnce(link) {
        // Touch devices have no hover, so play the icon animation once on reveal.
        if (!link || !link.querySelector('.tb-anim-icon')) return;
        if (window.matchMedia && window.matchMedia('(hover: hover)').matches) return;
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const icon = link.querySelector('.tb-anim-icon');
        icon.classList.add('is-playing');
        setTimeout(() => icon.classList.remove('is-playing'), 1200);
      }

      _activate(c) {
        const tb = this.topbar;
        const alreadyActive = this._activeId === c.id;

        tb.style.setProperty('--tb-col', c.color);
        tb.style.setProperty('--tb-darken', c.darken);
        tb.style.setProperty('--tb-pill', c.pill);
        tb.style.setProperty('--tb-fg', this._brightness(c.color) > 0.55 ? '#1b1b1b' : '#ffffff');
        tb.style.setProperty('--tb-pill-fg', this._brightness(c.pill) > 0.55 ? '#1b1b1b' : '#ffffff');

        // Add the class first so the slide is visible when <promo-rotator> connects
        // and measures slide heights (a hidden parent would measure 0).
        tb.classList.add('topbar--campaign-active');

        if (!alreadyActive) {
          this._buildRotator(c);
        }

        this._activeId = c.id;

        // Only (re)trigger confetti when a campaign first becomes active.
        if (!alreadyActive) this._maybeConfetti(c);
      }

      _deactivate() {
        const tb = this.topbar;
        tb.classList.remove('topbar--campaign-active');
        if (this.slide) this.slide.innerHTML = '';
        this._activeId = null;
      }

      _scheduleBoundaries() {
        // Re-evaluate exactly when any campaign's window opens or closes, so the
        // switch happens without requiring a page reload.
        const now = this._now();
        const boundaries = [];
        this.campaigns.forEach((c) => {
          if (!c.match) return;
          if (c.start != null && c.start > now) boundaries.push(c.start);
          if (c.end != null && c.end > now) boundaries.push(c.end);
        });
        boundaries.forEach((t) => {
          const ms = (t - now) * 1000 + 250;
          // setTimeout caps at ~24.8 days; skip anything further out.
          if (ms > 0 && ms < 2147483647) {
            this._boundaryTimers.push(setTimeout(() => this._evaluate(), ms));
          }
        });
      }

      _maybeConfetti(c) {
        if (!c.confetti) return;
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          return;
        }

        const key = 'tb-confetti-' + c.id;
        // In preview mode the session gate is skipped so the burst is visible on
        // every test reload.
        const inEditor = this._designMode || this._preview;

        if (!inEditor) {
          try {
            if (sessionStorage.getItem(key)) return;
          } catch (e) {
            /* storage blocked -> just run once for this page view */
          }
        }

        const delay = Math.max(0, c.delay || 0);
        if (this._confettiTimer) clearTimeout(this._confettiTimer);
        this._confettiTimer = setTimeout(() => {
          if (this._activeId !== c.id) return;
          if (document.hidden) return;
          if (!inEditor) {
            try {
              sessionStorage.setItem(key, '1');
            } catch (e) {
              /* ignore */
            }
          }
          this._runConfetti(c);
        }, delay);
      }

      _runConfetti(c) {
        const tb = this.topbar;
        const rect = tb.getBoundingClientRect();
        const depth = +c.depth || 200;

        const canvas = document.createElement('canvas');
        canvas.width = window.innerWidth;
        canvas.height = depth + 40;
        canvas.style.cssText =
          'position:fixed;left:0;top:' +
          Math.max(0, rect.top) +
          'px;pointer-events:none;z-index:99998;will-change:opacity';
        document.body.appendChild(canvas);

        const ctx = canvas.getContext('2d');
        const parts = [];
        const colors = ['#e01b22', '#ffffff', '#1b1b1b', c.pill || '#1b1b1b', '#fff3b0'];
        const EMIT_MS = 1400;
        // Cap the particle count and emit time-based (not per-frame) so a deeper
        // confetti setting or a slow frame can't balloon the main-thread cost.
        const MAX_PARTS = 90;
        const EMIT_PER_MS = MAX_PARTS / EMIT_MS;
        // Speeds are tuned at 60fps; scale by dt so a slow frame doesn't stretch the fade.
        const FRAME = 1000 / 60;
        const FADE_MS = 400;
        // Slowest flake (vy 1.2 px/frame) crossing `depth`, then a short canvas fade.
        const TOTAL_MS = EMIT_MS + (depth / 1.2) * FRAME;
        const FADE_AT = TOTAL_MS - FADE_MS;
        let emitAcc = 0;
        const t0 = performance.now();
        let last = t0;

        const spawn = () => {
          parts.push({
            x: Math.random() * canvas.width,
            y: Math.random() * 30,
            vy: 1.2 + Math.random() * 2,
            vx: (Math.random() - 0.5) * 1.2,
            r: Math.random() * 6,
            w: 6 + Math.random() * 6,
            h: 3 + Math.random() * 4,
            c: colors[Math.floor(Math.random() * colors.length)],
          });
        };

        const frame = (now) => {
          // Stop cleanly if the tab is backgrounded instead of piling up frames.
          if (document.hidden) {
            canvas.remove();
            return;
          }
          const dt = Math.min(48, now - last);
          last = now;
          const elapsed = now - t0;
          const k = dt / FRAME;

          // One compositor fade for the whole layer. Pieces stay opaque, so each
          // fillRect is a write instead of a per-piece blend. Stop as soon as the
          // layer is invisible — don't keep drawing the tail.
          if (elapsed >= FADE_AT) {
            canvas.style.opacity = String(Math.max(0, 1 - (elapsed - FADE_AT) / FADE_MS));
          }
          if (elapsed >= TOTAL_MS) {
            canvas.remove();
            return;
          }

          ctx.clearRect(0, 0, canvas.width, canvas.height);

          if (elapsed < EMIT_MS && parts.length < MAX_PARTS) {
            emitAcc += EMIT_PER_MS * dt;
            while (emitAcc >= 1 && parts.length < MAX_PARTS) {
              spawn();
              emitAcc -= 1;
            }
          }

          for (let i = parts.length - 1; i >= 0; i--) {
            if (parts[i].y >= depth) parts.splice(i, 1);
          }

          // setTransform is cheaper than save/translate/rotate/restore per particle.
          ctx.globalAlpha = 1;
          for (let i = 0; i < parts.length; i++) {
            const p = parts[i];
            p.y += p.vy * k;
            p.x += (p.vx + Math.sin(now / 300 + p.r) * 0.8) * k;
            p.r += 0.08 * k;
            const cos = Math.cos(p.r);
            const sin = Math.sin(p.r);
            ctx.setTransform(cos, sin, -sin, cos, p.x, p.y);
            ctx.fillStyle = p.c;
            ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          }
          ctx.setTransform(1, 0, 0, 1, 0, 0);

          requestAnimationFrame(frame);
        };

        requestAnimationFrame(frame);
      }
    }
  );
}
